import { useEffect, useRef, useCallback } from "react";
import { useCheckoutStore, type PaymentMode } from "../../store/checkout";
import { useCartStore } from "../../store/cart";
import { Modal } from "../shared/modal";
import { Spinner } from "../shared/spinner";
import { Button } from "../shared/button";
import { PayModal } from "./pay-modal";
import {
  apiPost,
  apiGet,
  endpoints,
} from "../../api/client";
import type {
  OrderResponse,
  CashPaymentResponse,
  TerminalCheckoutResponse,
  CheckoutStatusResponse,
  MerchantSettings,
} from "../../api/types";

function fmt(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

interface CheckoutFlowProps {
  settings: MerchantSettings;
  deviceId: string | null;
}

export function CheckoutFlow({ settings, deviceId }: CheckoutFlowProps) {
  const step = useCheckoutStore((s) => s.step);
  const paymentMode = useCheckoutStore((s) => s.paymentMode);
  const result = useCheckoutStore((s) => s.result);
  const errorMessage = useCheckoutStore((s) => s.errorMessage);
  const checkoutId = useCheckoutStore((s) => s.checkoutId);
  const {
    startChoosing,
    startProcessing,
    setOrderId,
    startPolling,
    succeed,
    fail,
    reset,
  } = useCheckoutStore();

  const items = useCartStore((s) => s.items);
  const cashSubtotal = useCartStore((s) => s.cashSubtotal);
  const clearCart = useCartStore((s) => s.clearCart);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Poll checkout status
  useEffect(() => {
    if (step !== "polling" || !checkoutId) return;

    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await apiGet<CheckoutStatusResponse>(
          endpoints.checkoutStatus(checkoutId),
        );
        if (res.status === "COMPLETED") {
          stopPolling();
          succeed({
            mode: paymentMode!,
            totalCents: 0,
            taxCents: 0,
            paymentId: res.payment_id,
          });
        } else if (res.status === "CANCELED" || res.status === "FAILED") {
          stopPolling();
          fail(
            res.status === "CANCELED"
              ? "Payment canceled on Terminal."
              : "Payment failed on Terminal.",
          );
        }
      } catch (err) {
        stopPolling();
        fail(err instanceof Error ? err.message : "Polling failed");
      }
    }, 2500);

    return stopPolling;
  }, [step, checkoutId, paymentMode, stopPolling, succeed, fail]);

  async function handleCharge(mode: PaymentMode) {
    startProcessing(mode);

    const feeRate = settings.fee_rate;
    const divisor = 1 - feeRate / 100;

    const orderItems = items.map((c) => {
      const cashP = c.originalPriceCents ?? c.basePriceCents;
      const cardP = Math.round(cashP / divisor);
      return {
        variation_id: c.variationId,
        name:
          c.name +
          (c.variationName && c.variationName !== "Regular"
            ? " \u00B7 " + c.variationName
            : ""),
        quantity: c.qty,
        base_price_cents: c.basePriceCents,
        bill_note: settings.bill_per_item
          ? `Cash ${fmt(cashP)} | Card ${fmt(cardP)}`
          : undefined,
      };
    });

    let billTotalsNote: string | undefined;
    if (settings.bill_totals) {
      const cashTotal = cashSubtotal();
      const cardTotal = items.reduce((sum, c) => {
        const base = c.originalPriceCents ?? c.basePriceCents;
        return sum + Math.round(base / divisor) * c.qty;
      }, 0);
      billTotalsNote = `Cash Total: ${fmt(cashTotal)} | Card Total: ${fmt(cardTotal)}`;
    }

    try {
      const orderRes = await apiPost<OrderResponse>(endpoints.createOrder, {
        mode,
        items: orderItems,
        fee_rate: feeRate,
        bill_totals_note: billTotalsNote,
      });

      setOrderId(orderRes.order_id);
      const totalCents = orderRes.total_cents;
      const taxCents = orderRes.tax_cents || 0;

      if (mode === "cash") {
        const cashRes = await apiPost<CashPaymentResponse>(
          endpoints.payCash(orderRes.order_id),
          {
            amount_cents: totalCents,
            order_id: orderRes.order_id,
          },
        );
        succeed({
          mode: "cash",
          totalCents,
          taxCents,
          paymentId: cashRes.payment_id,
        });
      } else {
        if (!deviceId) {
          fail("No Terminal paired — go to the Terminal tab");
          return;
        }
        const checkRes = await apiPost<TerminalCheckoutResponse>(
          endpoints.terminalCheckout,
          {
            device_id: deviceId,
            order_id: orderRes.order_id,
            amount_cents: totalCents,
            note: `Simple Cash Discount | Card incl. ${feeRate}% fee`,
          },
        );
        startPolling(checkRes.checkout_id);
      }
    } catch (err) {
      fail(err instanceof Error ? err.message : "Payment failed");
    }
  }

  async function handleCancel() {
    stopPolling();
    if (checkoutId) {
      try {
        await apiPost(endpoints.cancelCheckout(checkoutId));
      } catch {
        // Ignore cancel errors
      }
    }
    reset();
  }

  function handleNewSale() {
    reset();
    clearCart();
  }

  const isOpen = step !== "idle";

  return (
    <Modal open={isOpen} onClose={step === "choosing" ? reset : () => {}}>
      {step === "choosing" && (
        <PayModal
          feeRate={settings.fee_rate}
          mode={settings.mode}
          onCharge={handleCharge}
          onCancel={reset}
        />
      )}

      {(step === "processing" || step === "polling") && (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <Spinner />
          <div
            style={{ fontSize: "18px", fontWeight: 800, marginTop: "16px", marginBottom: "6px" }}
          >
            {paymentMode === "cash" ? "Recording cash payment..." : "Sending to Terminal..."}
          </div>
          <div style={{ fontSize: "13px", color: "var(--ink2)" }}>
            {step === "polling"
              ? "Customer tap or insert card"
              : "Please wait"}
          </div>
          <div style={{ marginTop: "20px" }}>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {step === "success" && result && (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>&#x2705;</div>
          <div style={{ fontSize: "20px", fontWeight: 800, marginBottom: "4px" }}>
            {result.mode === "cash" ? "Cash Payment" : "Card Payment"}
          </div>
          <div style={{ fontSize: "13px", color: "var(--ink2)", marginBottom: "16px" }}>
            Payment complete
          </div>
          <div style={{ fontSize: "24px", fontFamily: "var(--mono)", fontWeight: 700 }}>
            {fmt(result.totalCents)}
          </div>
          <Button full style={{ marginTop: "16px" }} onClick={handleNewSale}>
            New Sale
          </Button>
        </div>
      )}

      {step === "error" && (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>&#x274C;</div>
          <div style={{ fontSize: "18px", fontWeight: 800, marginBottom: "6px" }}>
            Payment Failed
          </div>
          <div style={{ fontSize: "13px", color: "var(--ink2)", marginBottom: "16px" }}>
            {errorMessage}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button variant="outline" style={{ flex: 1 }} onClick={reset}>
              Cancel
            </Button>
            <Button style={{ flex: 1 }} onClick={startChoosing}>
              Retry
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
